import { useState, useRef } from 'react';
import { RankingRoundStoryCard, type RoundMatchLine } from '../components/rankings/RankingRoundStoryCard';
import { generateShareImage } from '../utils/share';

const DEFAULT_MATCHES: RoundMatchLine[] = [
  { team1: 'Rafael + Maria', team2: 'Yago + Juliana', score: '6 x 3', isWo: false },
  { team1: 'Rafael + Yago', team2: 'Maria + Juliana', score: '7 x 5', isWo: false },
  { team1: 'Rafael + Juliana', team2: 'Maria + Yago', score: '3 x 6', isWo: false },
];

/**
 * Public, unauthenticated playground for experimenting with the round share card layout.
 * Renders the card at 1x with editable inputs so you can tweak numbers, copy, photo, etc.
 * The "PNG @ 1080×1920" button captures via modern-screenshot, same flow as real sharing.
 */
export function ShareCardPlaygroundPage() {
  const [rankingName, setRankingName] = useState('Panela Beach');
  const [roundNumber, setRoundNumber] = useState(7);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [rankingImageUrl, setRankingImageUrl] = useState<string | null>(null);
  const [matches, setMatches] = useState<RoundMatchLine[]>(DEFAULT_MATCHES);
  const [previewPngUrl, setPreviewPngUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onRankingImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRankingImageUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const updateMatch = (i: number, patch: Partial<RoundMatchLine>) => {
    setMatches(prev => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  };

  const handleCapture = async () => {
    if (!cardRef.current) return;
    setIsCapturing(true);
    setPreviewPngUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob = await generateShareImage(cardRef.current);
      if (blob) setPreviewPngUrl(URL.createObjectURL(blob));
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-blue text-white p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-display text-2xl font-bold mb-1">Share card playground</h1>
        <p className="text-sm text-white/50 mb-6">
          Unauthenticated. Edit inputs, tweak <code>RankingRoundStoryCard.tsx</code>, hot-reload, iterate.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-start">
          {/* Live card (360×640) */}
          <div className="flex flex-col gap-4">
            <div className="text-xs text-white/40 uppercase tracking-wider">Live preview — 1×</div>
            <div className="ring-1 ring-white/10 rounded-xl overflow-hidden shadow-2xl">
              <RankingRoundStoryCard
                ref={cardRef}
                rankingName={rankingName}
                roundNumber={roundNumber}
                rankingImageUrl={rankingImageUrl}
                photoDataUrl={photoDataUrl}
                matches={matches}
              />
            </div>

            <button
              onClick={handleCapture}
              disabled={isCapturing}
              className="px-4 py-2 bg-turquoise text-deep-blue font-semibold rounded-lg disabled:opacity-50 text-sm"
            >
              {isCapturing ? 'Capturing…' : 'Capture PNG @ 1080×1920'}
            </button>

            {previewPngUrl && (
              <div className="flex flex-col gap-2">
                <div className="text-xs text-white/40 uppercase tracking-wider">Captured PNG</div>
                <img
                  src={previewPngUrl}
                  alt="Captured"
                  className="w-[270px] ring-1 ring-white/10 rounded-xl"
                />
                <a
                  href={previewPngUrl}
                  download="share-card.png"
                  className="text-xs text-turquoise underline"
                >
                  download
                </a>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-col gap-5 max-w-md">
            <Field label="Ranking name">
              <input
                value={rankingName}
                onChange={(e) => setRankingName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm"
              />
            </Field>

            <Field label="Round number">
              <input
                type="number"
                value={roundNumber}
                onChange={(e) => setRoundNumber(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm"
              />
            </Field>

            <Field label="Photo (middle 3:4 slot)">
              <input type="file" accept="image/*" onChange={onPhotoChange} className="text-xs" />
              {photoDataUrl && (
                <button
                  onClick={() => setPhotoDataUrl(null)}
                  className="text-xs text-coral underline mt-1"
                >
                  clear
                </button>
              )}
            </Field>

            <Field label="Ranking image (bottom bar thumb)">
              <input type="file" accept="image/*" onChange={onRankingImageChange} className="text-xs" />
              {rankingImageUrl && (
                <button
                  onClick={() => setRankingImageUrl(null)}
                  className="text-xs text-coral underline mt-1"
                >
                  clear
                </button>
              )}
            </Field>

            <div className="pt-2 border-t border-white/10">
              <div className="text-xs text-white/40 uppercase tracking-wider mb-2">Matches</div>
              <div className="flex flex-col gap-3">
                {matches.map((m, i) => (
                  <div key={i} className="flex flex-col gap-1.5 p-3 rounded-lg bg-white/5">
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                      <input
                        value={m.team1}
                        onChange={(e) => updateMatch(i, { team1: e.target.value })}
                        className="px-2 py-1.5 rounded bg-white/10 border border-white/10 text-xs text-right"
                      />
                      <input
                        value={m.score}
                        onChange={(e) => updateMatch(i, { score: e.target.value })}
                        className="w-20 px-2 py-1.5 rounded bg-white/10 border border-white/10 text-xs text-center font-bold"
                      />
                      <input
                        value={m.team2}
                        onChange={(e) => updateMatch(i, { team2: e.target.value })}
                        className="px-2 py-1.5 rounded bg-white/10 border border-white/10 text-xs"
                      />
                    </div>
                    <label className="text-[11px] text-white/50 flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={m.isWo}
                        onChange={(e) => updateMatch(i, { isWo: e.target.checked })}
                      />
                      W.O.
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}
